/**
 * IndexedDB-backed scoped storage for the web shell.
 *
 * Design: one object store, composite key `${moduleId}::${key}`. We also keep
 * a `.sync_meta` row per moduleId that records per-key timestamps — same
 * shape as the Android sidecar file — so the sync engine (Phase B2) can
 * reconcile across platforms identically.
 *
 * All values are strings. Callers are responsible for JSON.stringify before
 * passing in (mirrors the Android bridge contract).
 */

const DB_NAME = 'shell';
const DB_VERSION = 2;
const STORE_KV = 'kv';
const STORE_META = 'meta';
const STORE_MODULES = 'modules';
const STORE_BINARY = 'binary';

interface ScopedKey {
  moduleId: string;
  key: string;
}

/**
 * A mutation broadcast to sibling Shell contexts over a BroadcastChannel.
 * Each context (launcher, every module iframe, warm-up frames) runs its own
 * StorageCache over the SHARED IndexedDB — without this, the launcher's cache
 * goes stale when a module writes, and the sync engine uploads nothing.
 */
type StorageBroadcast =
  | { type: 'set'; moduleId: string; key: string; value: string; ts: number }
  | { type: 'delete'; moduleId: string; key: string }
  | { type: 'meta'; moduleId: string; key: string; ts: number }
  | { type: 'putModule'; id: string; record: unknown }
  | { type: 'removeModule'; id: string };

let dbPromise: Promise<IDBDatabase> | null = null;

function openDb(): Promise<IDBDatabase> {
  if (dbPromise) return dbPromise;
  dbPromise = new Promise((resolve, reject) => {
    const req = indexedDB.open(DB_NAME, DB_VERSION);
    req.onupgradeneeded = () => {
      const db = req.result;
      if (!db.objectStoreNames.contains(STORE_KV)) db.createObjectStore(STORE_KV);
      if (!db.objectStoreNames.contains(STORE_META)) db.createObjectStore(STORE_META);
      if (!db.objectStoreNames.contains(STORE_MODULES)) db.createObjectStore(STORE_MODULES);
      if (!db.objectStoreNames.contains(STORE_BINARY)) db.createObjectStore(STORE_BINARY);
    };
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error ?? new Error('IndexedDB open failed'));
  });
  return dbPromise;
}

function scopedId({ moduleId, key }: ScopedKey): string {
  return `${moduleId}::${key}`;
}

/**
 * IndexedDB is async — but the Android bridge contract is synchronous for
 * loadData/saveData. We bridge this with an in-memory cache, warmed on
 * boot, that callers read synchronously. Writes go to both cache and DB
 * (DB write is fire-and-forget; ordering is preserved by serializing
 * through `pendingWrites`).
 */
class StorageCache {
  private kv = new Map<string, string>();
  private meta = new Map<string, Record<string, number>>();
  private modules = new Map<string, unknown>();
  private writeChain: Promise<void> = Promise.resolve();
  private ready = false;

  // Cross-context coherence (see StorageBroadcast). Lazily opened; null when
  // the browser lacks BroadcastChannel — the pre-sync refreshFromDb() still
  // guarantees correctness in that case.
  private channel: BroadcastChannel | null = null;
  private channelReady = false;

  async hydrate(): Promise<void> {
    const db = await openDb();
    await Promise.all([
      this.loadStore(db, STORE_KV, (k, v) => this.kv.set(String(k), String(v))),
      this.loadStore(db, STORE_META, (k, v) => this.meta.set(String(k), v as Record<string, number>)),
      this.loadStore(db, STORE_MODULES, (k, v) => this.modules.set(String(k), v)),
    ]);
    this.ready = true;
    // Start listening for sibling-context writes right after boot.
    this.ensureChannel();
  }

  isReady(): boolean { return this.ready; }

  // --- KV scoped storage ---
  get(scope: ScopedKey): string {
    return this.kv.get(scopedId(scope)) ?? '';
  }

  set(scope: ScopedKey, value: string): void {
    const id = scopedId(scope);
    const ts = Date.now();
    this.kv.set(id, value);
    this.bumpMeta(scope.moduleId, scope.key, ts);
    this.queueWrite(STORE_KV, id, value);
    this.broadcast({ type: 'set', moduleId: scope.moduleId, key: scope.key, value, ts });
  }

  delete(scope: ScopedKey): void {
    const id = scopedId(scope);
    this.kv.delete(id);
    this.dropMeta(scope.moduleId, scope.key);
    this.queueDelete(STORE_KV, id);
    this.broadcast({ type: 'delete', moduleId: scope.moduleId, key: scope.key });
  }

  // --- Meta (per-module per-key timestamps) ---
  private bumpMeta(moduleId: string, key: string, ms: number): void {
    const m = this.meta.get(moduleId) ?? {};
    m[key] = ms;
    this.meta.set(moduleId, m);
    this.queueWrite(STORE_META, moduleId, m);
  }

  private dropMeta(moduleId: string, key: string): void {
    const m = this.meta.get(moduleId);
    if (!m) return;
    delete m[key];
    this.meta.set(moduleId, m);
    this.queueWrite(STORE_META, moduleId, m);
  }

  getMeta(moduleId: string): Record<string, number> {
    return { ...(this.meta.get(moduleId) ?? {}) };
  }

  /**
   * Override the auto-bumped timestamp for a key after a sync pull —
   * the engine needs to record the AUTHORITATIVE remote timestamp, not
   * "now", so the next sync compares correctly.
   */
  setMetaTimestamp(moduleId: string, key: string, ms: number): void {
    const m = this.meta.get(moduleId) ?? {};
    m[key] = ms;
    this.meta.set(moduleId, m);
    this.queueWrite(STORE_META, moduleId, m);
    this.broadcast({ type: 'meta', moduleId, key, ts: ms });
  }

  /**
   * All keys this module has stored. Used by the sync engine to enumerate
   * what to upload. Includes published-namespace entries; callers must
   * filter as appropriate.
   */
  listKeysForModule(moduleId: string): string[] {
    const prefix = `${moduleId}::`;
    const keys: string[] = [];
    for (const composite of this.kv.keys()) {
      if (composite.startsWith(prefix)) keys.push(composite.slice(prefix.length));
    }
    return keys;
  }

  // --- Modules registry ---
  getModule(id: string): unknown {
    return this.modules.get(id);
  }

  listModules(): Array<[string, unknown]> {
    return Array.from(this.modules.entries());
  }

  putModule(id: string, record: unknown): void {
    this.modules.set(id, record);
    this.queueWrite(STORE_MODULES, id, record);
    this.broadcast({ type: 'putModule', id, record });
  }

  removeModule(id: string): void {
    this.modules.delete(id);
    this.queueDelete(STORE_MODULES, id);
    this.broadcast({ type: 'removeModule', id });
  }

  // --- Binary storage (audio recordings, images, etc.) ---
  // Blobs can be megabytes — we don't cache them in memory. All ops are async.

  async putBinary(scope: ScopedKey, blob: Blob): Promise<void> {
    const id = scopedId(scope);
    const db = await openDb();
    await new Promise<void>((resolve, reject) => {
      const tx = db.transaction(STORE_BINARY, 'readwrite');
      tx.objectStore(STORE_BINARY).put(blob, id);
      tx.oncomplete = () => resolve();
      tx.onerror = () => reject(tx.error ?? new Error('IDB binary write failed'));
    });
  }

  async getBinary(scope: ScopedKey): Promise<Blob | null> {
    const id = scopedId(scope);
    const db = await openDb();
    return new Promise<Blob | null>((resolve, reject) => {
      const tx = db.transaction(STORE_BINARY, 'readonly');
      const req = tx.objectStore(STORE_BINARY).get(id);
      req.onsuccess = () => {
        const value = req.result;
        resolve(value instanceof Blob ? value : null);
      };
      req.onerror = () => reject(req.error ?? new Error('IDB binary read failed'));
    });
  }

  async deleteBinary(scope: ScopedKey): Promise<void> {
    const id = scopedId(scope);
    const db = await openDb();
    await new Promise<void>((resolve, reject) => {
      const tx = db.transaction(STORE_BINARY, 'readwrite');
      tx.objectStore(STORE_BINARY).delete(id);
      tx.oncomplete = () => resolve();
      tx.onerror = () => reject(tx.error ?? new Error('IDB binary delete failed'));
    });
  }

  /** List all binary file names stored under moduleId. Used for cleanup / UI. */
  async listBinaryForModule(moduleId: string): Promise<string[]> {
    const db = await openDb();
    const prefix = `${moduleId}::`;
    return new Promise<string[]>((resolve, reject) => {
      const tx = db.transaction(STORE_BINARY, 'readonly');
      const req = tx.objectStore(STORE_BINARY).openCursor();
      const out: string[] = [];
      req.onsuccess = () => {
        const cursor = req.result;
        if (cursor) {
          const k = String(cursor.key);
          if (k.startsWith(prefix)) out.push(k.slice(prefix.length));
          cursor.continue();
        } else resolve(out);
      };
      req.onerror = () => reject(req.error ?? new Error('IDB binary scan failed'));
    });
  }

  // --- Write serialization ---
  private queueWrite(store: string, key: string, value: unknown): void {
    this.writeChain = this.writeChain.then(async () => {
      const db = await openDb();
      await new Promise<void>((resolve, reject) => {
        const tx = db.transaction(store, 'readwrite');
        tx.objectStore(store).put(value, key);
        tx.oncomplete = () => resolve();
        tx.onerror = () => reject(tx.error ?? new Error('IDB write failed'));
      });
    }).catch((err) => console.error('[shell.storage] write failed', err));
  }

  private queueDelete(store: string, key: string): void {
    this.writeChain = this.writeChain.then(async () => {
      const db = await openDb();
      await new Promise<void>((resolve, reject) => {
        const tx = db.transaction(store, 'readwrite');
        tx.objectStore(store).delete(key);
        tx.oncomplete = () => resolve();
        tx.onerror = () => reject(tx.error ?? new Error('IDB delete failed'));
      });
    }).catch((err) => console.error('[shell.storage] delete failed', err));
  }

  private loadStore(
    db: IDBDatabase,
    store: string,
    sink: (k: IDBValidKey, v: unknown) => void,
  ): Promise<void> {
    return new Promise((resolve, reject) => {
      const tx = db.transaction(store, 'readonly');
      const req = tx.objectStore(store).openCursor();
      req.onsuccess = () => {
        const cursor = req.result;
        if (cursor) {
          sink(cursor.key, cursor.value);
          cursor.continue();
        } else {
          resolve();
        }
      };
      req.onerror = () => reject(req.error ?? new Error('IDB scan failed'));
    });
  }

  // --- Cross-context coherence + pre-sync refresh ---

  private ensureChannel(): void {
    if (this.channelReady) return;
    this.channelReady = true;
    if (typeof BroadcastChannel === 'undefined') return;
    try {
      this.channel = new BroadcastChannel('shell-storage');
      this.channel.onmessage = (ev) => this.applyRemote(ev.data as StorageBroadcast);
    } catch {
      this.channel = null;
    }
  }

  private broadcast(msg: StorageBroadcast): void {
    this.ensureChannel();
    try { this.channel?.postMessage(msg); } catch { /* channel closed */ }
  }

  /**
   * Apply a mutation made in another Shell context. Updates the in-memory
   * maps ONLY — the originating context already persisted to IndexedDB, so we
   * must not re-queue a write (duplicate) or re-broadcast (would loop).
   */
  private applyRemote(msg: StorageBroadcast): void {
    if (!msg || typeof msg !== 'object') return;
    switch (msg.type) {
      case 'set': {
        this.kv.set(scopedId({ moduleId: msg.moduleId, key: msg.key }), msg.value);
        const m = this.meta.get(msg.moduleId) ?? {};
        m[msg.key] = msg.ts;
        this.meta.set(msg.moduleId, m);
        break;
      }
      case 'delete': {
        this.kv.delete(scopedId({ moduleId: msg.moduleId, key: msg.key }));
        const m = this.meta.get(msg.moduleId);
        if (m) { delete m[msg.key]; this.meta.set(msg.moduleId, m); }
        break;
      }
      case 'meta': {
        const m = this.meta.get(msg.moduleId) ?? {};
        m[msg.key] = msg.ts;
        this.meta.set(msg.moduleId, m);
        break;
      }
      case 'putModule':
        this.modules.set(msg.id, msg.record);
        break;
      case 'removeModule':
        this.modules.delete(msg.id);
        break;
    }
  }

  /**
   * Rebuild the in-memory cache from IndexedDB (the source of truth shared by
   * every context). Called right before a sync as a safety net: even if a
   * BroadcastChannel message was missed (unsupported browser, suspended tab),
   * the launcher then reconciles against what modules ACTUALLY persisted —
   * never against a stale cache. Builds fresh maps and swaps them atomically,
   * so a concurrent synchronous read never observes a half-loaded state.
   */
  async refreshFromDb(): Promise<void> {
    if (!this.ready) return;
    await this.flush(); // our own queued writes land before we re-read
    const db = await openDb();
    const kv = new Map<string, string>();
    const meta = new Map<string, Record<string, number>>();
    const modules = new Map<string, unknown>();
    await Promise.all([
      this.loadStore(db, STORE_KV, (k, v) => kv.set(String(k), String(v))),
      this.loadStore(db, STORE_META, (k, v) => meta.set(String(k), v as Record<string, number>)),
      this.loadStore(db, STORE_MODULES, (k, v) => modules.set(String(k), v)),
    ]);
    this.kv = kv;
    this.meta = meta;
    this.modules = modules;
  }

  /** Wait for any pending writes to flush. Useful before navigation/unload. */
  async flush(): Promise<void> {
    await this.writeChain;
  }
}

export const storage = new StorageCache();
